/**
 * `/coaching/library` — the content library, for the people who review
 * it.
 *
 * The grain is the CATALOG UNIT: the only thing a learner can be dealt,
 * and so the only thing an author authors and the only thing a human
 * reviews.  The instruments have not gone anywhere — they are the
 * priceable instruments, and every unit's panel lists the ones it embeds — but
 * an instrument is not something anybody can be offered, so it was never the
 * right thing to walk a shelf of.
 *
 * The press publishes content authored by teams who are not in the
 * room.  Gate 1 checks its grammar and gate 2 replays its recorded
 * trace, and between them they have not read a word of it.  This is
 * where a human does: walk the atlas the way it is drawn (ring →
 * planet → claim), read what stands on each shelf, and record a
 * verdict so the next reviewer starts where this one stopped.
 *
 * Two things it deliberately does NOT do.  It does not reuse the Django
 * admin's chrome — only its permission, which is the same `is_staff`
 * door, enforced on the API rather than here (a frontend gate decides
 * what is drawn; it cannot decide what is served).  And it does not
 * show answer keys: those are stripped server-side at every door, and a
 * staff page is not a reason to open a wider one.
 *
 * The URL carries the whole view — scope, chips, search — so a
 * reviewer can send a colleague the exact shelf they are looking at.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { FilterBar } from "./library/FilterBar";
import { PipelineView } from "./library/PipelineView";
import { PublishFiles } from "./library/PublishFiles";
import { RepublishDialog } from "./library/RepublishDialog";
import { ShelfBadge, ShelfNav } from "./library/ShelfNav";
import { UnitDetail } from "./library/UnitDetail";
import { UnitList } from "./library/UnitList";
import {
  fromQueryString,
  scopeCrumbs,
  scopeTo,
  toQueryString,
  type Chips,
  type LibraryQuery,
  type Scope,
} from "./library/filters";
import type { PlanetShelf } from "./library/types";
import {
  exportNotes,
  useShelves,
  useStacked,
  useUnitDetail,
  useUnits,
  type Failure,
} from "./library/useLibrary";
import { Badge, Button, Check, Id, Label, Menu, MenuItem, Modal, MONO, Panel } from "./library/ui";

const PAGE = 100;

/** Find the planet the reader is standing on, wherever it is shelved. */
function findPlanet(
  shelves: { rings: { planets: PlanetShelf[] }[]; outside: PlanetShelf[] } | null,
  planetId: string,
): PlanetShelf | null {
  if (!shelves || !planetId) return null;
  for (const ring of shelves.rings) {
    const hit = ring.planets.find((planet) => planet.id === planetId);
    if (hit) return hit;
  }
  return shelves.outside.find((planet) => planet.id === planetId) ?? null;
}

function Denied({ failure }: { failure: Failure }) {
  const forbidden = failure.kind === "forbidden";
  return (
    <div style={{ maxWidth: 460, margin: "18vh auto", padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 8, color: "var(--color-text)" }}>
        {forbidden ? "This page is for administrators" : "You are signed out"}
      </div>
      <p style={{ fontSize: 13.5, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
        {forbidden
          ? "The content library shows unpublished editorial state, so it is limited to staff accounts. If you should have access, ask an administrator to grant it."
          : "Sign in with a staff account to read the content library."}
      </p>
      {forbidden ? null : (
        <a
          href={`/account/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
          style={{ color: "var(--color-primary)", fontSize: 13.5 }}
        >
          Go to sign in
        </a>
      )}
      {failure.kind === "error" ? (
        <div style={{ marginTop: 10, fontFamily: MONO, fontSize: 12, color: "var(--color-error)" }}>
          {failure.detail}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A section that is a disclosure on a phone and plain content on a desk.
 *
 * One component rather than two branches at the call site, so the tree
 * inside it is written once and cannot drift between the two widths.
 */
function Folded({
  folds,
  open,
  label,
  onToggle,
  children,
}: {
  folds: boolean;
  open: boolean;
  label: string;
  onToggle: () => void;
  children: ReactNode;
}) {
  if (!folds) return <>{children}</>;
  return (
    <>
      <button
        type="button"
        data-testid="shelf-disclosure"
        aria-expanded={open}
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          minHeight: 40,
          padding: "6px 10px",
          borderRadius: 8,
          border: "1px solid var(--color-border)",
          background: "transparent",
          color: "var(--color-text)",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 13,
          textAlign: "left",
        }}
      >
        <span style={{ color: "var(--color-text-muted)" }}>{open ? "▾" : "▸"}</span>
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </span>
        <span style={{ fontSize: 11.5, color: "var(--color-text-muted)" }}>shelves</span>
      </button>
      {open ? <div style={{ marginTop: 8 }}>{children}</div> : null}
    </>
  );
}

function Stat({ value, name, tone, hint }: { value: number; name: string; tone?: string; hint?: string }) {
  return (
    <div style={{ minWidth: 74 }} title={hint}>
      <div style={{ fontFamily: MONO, fontSize: 18, color: tone ?? "var(--color-text)" }}>{value}</div>
      <Label>{name}</Label>
    </div>
  );
}

export function LibraryPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const search = params.toString();

  // The address bar is the state.  One source, so a pasted link and a
  // click through the tree cannot produce different views.
  const query = useMemo<LibraryQuery>(() => fromQueryString(search), [search]);
  const [draft, setDraft] = useState(query.search);
  const [limit, setLimit] = useState(PAGE);
  // Both the open panel and the selection are keyed by the PERMANENT
  // name, never the address: a republish moves every address on the
  // shelf, and a reader would lose their place and their selection for a
  // reason nothing on screen would explain.
  const [openRef, setOpenRef] = useState<string | null>(null);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [onlyUnreviewed, setOnlyUnreviewed] = useState(false);
  const stacked = useStacked();
  const [navOpen, setNavOpen] = useState(false);
  // The selection is deliberately NOT in the URL.  Everything else on
  // this page is shareable state ("look at this shelf"); a selection is
  // a working set of one person's next few minutes, and a link carrying
  // three hundred refs would be neither shareable nor readable.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [exported, setExported] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [republishing, setRepublishing] = useState<string[] | null>(null);

  const push = useCallback(
    (next: LibraryQuery) => {
      const qs = toQueryString(next);
      navigate({ search: qs ? `?${qs}` : "" }, { replace: false });
      setLimit(PAGE);
    },
    [navigate],
  );

  useEffect(() => setDraft(query.search), [query.search]);

  // Search is debounced into the URL: typing should filter, and a
  // history entry per keystroke would make the back button useless.
  useEffect(() => {
    if (draft === query.search) return undefined;
    const timer = window.setTimeout(() => {
      const qs = toQueryString({ ...query, search: draft });
      navigate({ search: qs ? `?${qs}` : "" }, { replace: true });
      setLimit(PAGE);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [draft, query, navigate]);

  const shelves = useShelves(query.pipeline);
  const units = useUnits(query, limit);
  const rows = units.data?.items ?? [];
  const openRow = rows.find((row) => row.permanentRef === openRef) ?? null;
  // The ADDRESS when the row is on screen, the name otherwise: the
  // address pins the version the reader is actually looking at, and the
  // name is all there is to go on before the list lands.
  const detail = useUnitDetail(openRow?.unitRef ?? openRef);

  /** The registry or a verdict changed — every count on screen is stale. */
  const reload = useCallback(() => {
    shelves.reload();
    units.reload();
    detail.reload();
  }, [shelves, units, detail]);

  const failure = shelves.failure;
  if (failure) return <Denied failure={failure} />;

  const planet = findPlanet(shelves.data, query.scope.planet);
  const crumbs = scopeCrumbs(query.scope);

  const onScope = (scope: Partial<Scope>) => {
    push(scopeTo(query, scope));
    setOpenRef(null);
  };
  const onChips = (chips: Chips) => push({ ...query, chips });
  // The pipeline view survives the scope, so it is set on the query
  // itself rather than through ``scopeTo`` — see ``filters.ts``.
  const onPipeline = (pipeline: string) => push({ ...query, pipeline });

  const onPick = (ref: string, next: boolean) =>
    setPicked((was) => {
      const now = new Set(was);
      if (next) now.add(ref);
      else now.delete(ref);
      return now;
    });

  const onPickAll = (next: boolean) =>
    setPicked((was) => {
      const now = new Set(was);
      for (const row of rows) {
        if (next) now.add(row.permanentRef);
        else now.delete(row.permanentRef);
      }
      return now;
    });

  const onExport = async () => {
    const sent = await exportNotes([...picked]);
    setExported(sent.ok ? sent.data.text : `could not export: ${sent.detail}`);
  };

  const pickedHere = rows.filter((row) => picked.has(row.permanentRef)).length;

  return (
    <div
      data-testid="library-page"
      style={{
        // The page is its OWN scroll container on a desk, and that is
        // load-bearing rather than stylistic.  The app shell's `#root`
        // carries `overflow-x: hidden` (it stops horizontal overflow on
        // phones), which computes `overflow-y: auto` and makes `#root`
        // the scrolling ancestor of everything inside it.  A
        // `position: sticky` panel therefore sticks to a box that is as
        // tall as the whole document and so never moves — the shelf
        // navigator and the detail panel scrolled away the moment the
        // unit list got long.  Scrolling here instead gives them an
        // ancestor that actually scrolls.  On a phone the layout is
        // one column and nothing is sticky, so the page scrolls
        // normally and no nested scroller gets in the way of the
        // browser's own gestures.
        height: stacked ? undefined : "var(--app-100vh, 100vh)",
        minHeight: stacked ? "100vh" : undefined,
        overflowY: stacked ? undefined : "auto",
        // Subtle, not --color-bg: in the light scheme the page ground
        // and the panel ground are both white, so a page painted with
        // the first would make every panel edge disappear into it.
        background: "var(--color-bg-subtle)",
        color: "var(--color-text)",
        padding: "20px clamp(12px, 3vw, 32px) 48px",
        boxSizing: "border-box",
      }}
    >
      <header style={{ display: "flex", alignItems: "flex-end", gap: 20, flexWrap: "wrap", marginBottom: 18 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 style={{ margin: 0, fontSize: 21, fontWeight: 650, letterSpacing: "-0.01em" }}>
            Content library
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
            Every unit the press can deal, shelved the way the atlas is drawn — and what a human has
            said about it.
          </p>
        </div>
        <Button testId="publish-files-open" kind="primary" onClick={() => setPublishing(true)}>
          Publish files…
        </Button>
        {shelves.data ? (
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }} data-testid="library-totals">
            <Stat value={shelves.data.totals.units} name="units" hint="the offerable grain — what a learner can be dealt" />
            <Stat
              value={shelves.data.totals.instruments}
              name="instruments"
              hint="the distinct priceable instruments embedded across the shelf"
            />
            <Stat
              value={shelves.data.totals.outsideUnits}
              name="outside the rings"
              tone={shelves.data.totals.outsideUnits ? "var(--color-warning-text)" : undefined}
            />
            <Stat value={shelves.data.totals.planetsPlaced} name="planets on rings" />
            <Stat value={shelves.data.totals.unread} name="unread" />
            <Stat
              value={shelves.data.totals.flagged}
              name="flagged"
              tone={shelves.data.totals.flagged ? "var(--color-warning-text)" : undefined}
            />
          </div>
        ) : null}
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: stacked
            ? "minmax(0, 1fr)"
            : openRow
              ? "minmax(220px, 260px) minmax(0, 1fr) minmax(280px, 360px)"
              : "minmax(220px, 260px) minmax(0, 1fr)",
          gap: 16,
          // NOT align-items:start.  A grid item that shrinks to its
          // content ends where its content ends, and a sticky child
          // stops sticking there — so the shelf navigator scrolled away
          // the moment the unit list was taller than it.  The items
          // stretch; the panels inside them stick.
        }}
      >
        <div>
          <Panel
            style={{
              padding: 12,
              position: stacked ? "static" : "sticky",
              top: 16,
              maxHeight: stacked ? undefined : "calc(var(--app-100vh, 100vh) - 40px)",
              overflow: "auto",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
                fontSize: 12.5,
                color: "var(--color-text-secondary)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                data-testid="only-unreviewed"
                checked={onlyUnreviewed}
                onChange={(event) => setOnlyUnreviewed(event.target.checked)}
              />
              only what needs a read
            </label>
            {shelves.data ? (
              // On a phone the tree is folded away by default.  Thirty-four
              // planets and their rosters stacked above the shelf is a
              // screen and a half of navigation before the first unit —
              // the reader would scroll past the whole atlas to reach
              // what they came to read.
              <Folded
                open={!stacked || navOpen}
                onToggle={() => setNavOpen((was) => !was)}
                folds={stacked}
                label={crumbs.length ? crumbs.join(" › ") : "Everything"}
              >
                <ShelfNav
                  shelves={shelves.data}
                  query={query}
                  onScope={(scope) => {
                    onScope(scope);
                    if (stacked) setNavOpen(false);
                  }}
                  onlyUnreviewed={onlyUnreviewed}
                />
              </Folded>
            ) : (
              <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>loading the atlas…</div>
            )}
          </Panel>
        </div>

        <div style={{ display: "grid", gap: 12, minWidth: 0, alignContent: "start" }}>
          <Panel style={{ padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              <nav aria-label="where you are" data-testid="library-crumbs" style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => onScope({})}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                    color: crumbs.length ? "var(--color-primary)" : "var(--color-text)",
                    fontFamily: "inherit",
                    fontSize: 13,
                  }}
                >
                  Everything
                </button>
                {crumbs.map((crumb) => (
                  <span key={crumb} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    <span style={{ color: "var(--color-text-muted)" }}>›</span>
                    <Id style={{ fontSize: 12.5 }}>{crumb}</Id>
                  </span>
                ))}
              </nav>

              <span style={{ flex: 1 }} />

              <PipelineView
                value={query.pipeline}
                pipelines={
                  units.data?.vocabulary?.pipeline ?? shelves.data?.vocabulary?.pipeline
                }
                counts={units.data?.facets?.pipeline}
                onChange={onPipeline}
              />

              <input
                data-testid="library-search"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="search title, id, claim, file…"
                style={{
                  minWidth: 200,
                  minHeight: 32,
                  padding: "5px 10px",
                  borderRadius: 8,
                  border: "1px solid var(--color-border)",
                  background: "var(--color-bg-subtle)",
                  color: "var(--color-text)",
                  fontFamily: "inherit",
                  fontSize: 13,
                }}
              />
              {query.search ? (
                <Button onClick={() => push({ ...query, search: "" })}>clear search</Button>
              ) : null}
            </div>

            <FilterBar
              chips={query.chips}
              facets={units.data?.facets ?? null}
              vocabulary={units.data?.vocabulary ?? shelves.data?.vocabulary ?? null}
              onChange={onChips}
              showAll={showAllFilters}
              onShowAll={setShowAllFilters}
            />
          </Panel>

          <Panel style={{ overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderBottom: "1px solid var(--color-border-subtle)",
              }}
            >
              <Check
                testId="library-pick-all"
                checked={rows.length > 0 && pickedHere === rows.length}
                indeterminate={pickedHere > 0 && pickedHere < rows.length}
                onChange={onPickAll}
                label="select every row on this shelf"
              />
              <Label>
                {units.data
                  ? `${units.data.total} of ${units.data.scoped} on this shelf`
                  : "loading…"}
              </Label>
              {picked.size ? (
                <Badge tone="accent" testId="library-picked-count">
                  {picked.size} selected
                </Badge>
              ) : null}
              <span style={{ flex: 1 }} />
              {planet ? <ShelfBadge planet={planet} /> : null}
              {/* Beside "more filters", where the ask put it: the
                  actions a reviewer takes on a SELECTION rather than on
                  the shelf as a whole. */}
              <Menu label="Actions ▾" testId="library-actions" align="right">
                {(close) => (
                  <>
                    <MenuItem
                      testId="library-export"
                      disabled={picked.size === 0}
                      onClick={() => {
                        close();
                        void onExport();
                      }}
                    >
                      Export comments ({picked.size})
                    </MenuItem>
                    <MenuItem
                      testId="library-clear-selection"
                      disabled={picked.size === 0}
                      onClick={() => {
                        close();
                        setPicked(new Set());
                      }}
                    >
                      Clear selection
                    </MenuItem>
                  </>
                )}
              </Menu>
            </div>
            <UnitList
              items={rows}
              total={units.data?.total ?? 0}
              shown={rows.length}
              openRef={openRef}
              picked={picked}
              pipelines={units.data?.vocabulary?.pipeline ?? shelves.data?.vocabulary?.pipeline}
              onOpen={(ref) => setOpenRef((was) => (was === ref ? null : ref))}
              onPick={onPick}
              onMore={() => setLimit((was) => was + PAGE)}
              loading={units.status === "loading"}
            />
          </Panel>
        </div>

        {openRow ? (
          <div>
            <UnitDetail
              stacked={stacked}
              row={openRow}
              detail={detail.data}
              failure={detail.failure}
              loading={detail.status === "loading"}
              onClose={() => setOpenRef(null)}
              onReviewed={reload}
              onRepublish={(filename) => setRepublishing([filename])}
            />
          </div>
        ) : null}
      </div>

      {publishing ? (
        <PublishFiles
          onClose={() => setPublishing(false)}
          onChosen={(filenames) => {
            setPublishing(false);
            setRepublishing(filenames);
          }}
        />
      ) : null}

      {republishing ? (
        <RepublishDialog
          filenames={republishing}
          onClose={() => setRepublishing(null)}
          onDone={reload}
        />
      ) : null}

      {exported !== null ? (
        <Modal
          testId="export-modal"
          title="Comments on the selected units"
          onClose={() => setExported(null)}
          footer={
            <>
              <Button
                testId="export-copy"
                onClick={() => {
                  // `writeText` is unavailable outside a secure context
                  // and can be denied even inside one, so the textarea
                  // below is the real deliverable and this is the
                  // convenience — it must never be the only way out.
                  void navigator.clipboard?.writeText(exported).catch(() => undefined);
                }}
              >
                Copy
              </Button>
              <Button kind="primary" onClick={() => setExported(null)}>
                Close
              </Button>
            </>
          }
        >
          {exported ? (
            <textarea
              data-testid="export-text"
              readOnly
              value={exported}
              rows={16}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 8,
                border: "1px solid var(--color-border)",
                background: "var(--color-bg-subtle)",
                color: "var(--color-text)",
                fontFamily: MONO,
                fontSize: 12,
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
          ) : (
            <div data-testid="export-empty" style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
              None of the selected units carry current feedback. Notes written against content that
              has since been purged or republished are kept as history, but are deliberately left
              out of an export — an author sent one would go looking for something that is no longer
              there.
            </div>
          )}
        </Modal>
      ) : null}
    </div>
  );
}

export default LibraryPage;
